import { motion } from "framer-motion";
import type { ReactNode } from "react";
import { useRef } from "react";

interface ButtonProps {
  children: ReactNode;
  onClick?: () => void;
  variant?: "primary" | "outline" | "ghost";
  full?: boolean;
  className?: string;
  disabled?: boolean;
}

/** Editorial button. Primary = charcoal block, outline = hairline, ghost = text. */
export function Button({ children, onClick, variant = "outline", full, className = "", disabled }: ButtonProps) {
  const base =
    "inline-flex items-center justify-center gap-2 px-4 py-3 font-display text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors disabled:opacity-40";
  const styles = {
    primary: "bg-primary text-on-primary hover:bg-surface-tint",
    outline: "rounded-lg border border-outline-variant/60 bg-surface text-primary hover:border-primary",
    ghost: "text-on-surface-variant hover:text-primary",
  }[variant];
  return (
    <motion.button
      type="button"
      whileHover={disabled ? undefined : { scale: 1.01 }}
      whileTap={disabled ? undefined : { scale: 0.98 }}
      disabled={disabled}
      onClick={onClick}
      className={`${base} ${styles} ${full ? "w-full" : ""} ${className}`}
    >
      {children}
    </motion.button>
  );
}

interface FileButtonProps {
  children: ReactNode;
  accept: string;
  onFile: (file: File) => void;
  variant?: "primary" | "outline" | "ghost";
  full?: boolean;
}

/** Button that opens a native file picker and forwards the selected file. */
export function FileButton({ children, accept, onFile, variant = "outline", full }: FileButtonProps) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <Button variant={variant} full={full} onClick={() => ref.current?.click()}>
        {children}
      </Button>
      <input
        ref={ref}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = "";
        }}
      />
    </>
  );
}
